import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";
import PhotoBriefPlaceholder from "./PhotoBriefPlaceholder";
import { PHOTO_BRIEFS } from "../data/photoBriefs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_META = {
  "pressure-washing": {
    blurb: "Calibrated soft-wash and hot-water systems restore brick, stucco, siding, and stone without damage. Grease and gum vanish. Facades look ten years younger.",
    image: null,
    location: "Brownstone · Brooklyn, NY",
    how: ["Site survey + surface identification (brick, stucco, siding, stone)", "Water-capture berms installed to protect landscaping + storm drains", "Hot-water rig or soft-wash system selected per substrate", "Neutralizing rinse + curb sweep before demobilizing", "Photo debrief with before/after per zone"],
    equipment: "5.6 GPM commercial rig · hot-water box · surface cleaner · soft-wash injectors",
  },
  "awning-cleaning": {
    blurb: "Fabric, metal, and glass awnings brightened without damaging seams, coatings, or graphics. Storefront-ready in a single scheduled visit.",
    image: null,
    location: "Storefront · West Village, NY",
    how: ["Material identification (Sunbrella, vinyl, aluminum, glass)", "pH-neutral cleaner applied at low pressure — never blasting", "Soft brush agitation for seams and stitched edges", "Rinse + air-dry to prevent water spots on graphics", "Optional Scotchgard re-treatment on fabric"],
    equipment: "Low-pressure garden pump · pH-neutral biodegradable soap · natural bristle brushes",
  },
  "window-cleaning": {
    blurb: "Ground-level to high-rise. IWCA-standard safety protocols. Streak-free glass, polished hardware, no residue. Interior + exterior packages.",
    image: null,
    location: "Storefront · Manhattan, NY",
    how: ["Pre-clean walkthrough + safety brief (RIA/IWCA)", "Frame + track brushed, hardware polished", "Squeegee technique with T-bar strip washer, no streaks", "Sill wiped, glass inspected under angle", "Photo debrief + optional Rain-X on exterior at request"],
    equipment: "Water-fed pole (up to 65 ft) · squeegees · microfiber cloths · certified rigs for high-rise",
  },
  "sidewalk-cleaning": {
    blurb: "Gum removal, oil lifting, stain elimination. Municipal-code aware. Coordinated with your posted-hours to keep foot traffic uninterrupted.",
    image: null,
    location: "Sidewalk · SoHo, NY",
    how: ["Debris sweep + trash removal", "Gum & oil pre-treatment (biodegradable degreaser)", "Hot-water surface-cleaner pass at ~1,500 PSI", "Perimeter edge detail (curbs, tree pits)", "Municipal wastewater capture — no runoff to storm drains"],
    equipment: "24\" surface cleaner · hot-water rig · vac-recovery unit · sidewalk-safe detergents",
  },
  "parking-lot-striping": {
    blurb: "New layouts, refresh striping, ADA/handicap markings, curbs, arrows, and stenciling. Fast-cure paint. Overnight turnaround available.",
    image: null,
    location: "Retail Center · Charlotte, NC",
    how: ["Layout plan + ADA compliance review", "Sweep + high-pressure clean of the lot", "Laser-guided line-lock chalking", "Airless-spray latex or oil-based traffic paint", "Fast-cure (30–60 min) — lot back open by morning"],
    equipment: "Airless line striper · laser layout kit · industrial stencils · fast-cure paint",
  },
  "parking-lot-sweeping": {
    blurb: "Scheduled sweeping cadence. Debris hauled off-site. Retail, medical, industrial, and HOA properties on a documented rotation.",
    image: null,
    location: "Medical Campus · Raleigh, NC",
    how: ["Mechanized sweeper deploys during off-hours", "Curbs, corners, and tree pits detailed by hand", "Debris hauled off-site — no dumpster overflow", "Blower finish across walkways", "Route documented + timestamped"],
    equipment: "Ride-on regenerative-air sweeper · hand blower · debris bin trailer",
  },
  "snow-removal": {
    blurb: "Pre-treat, plow, shovel, salt. Site maps documented per property. Storm-response teams staged during declared events. 24/7.",
    image: null,
    location: "Storefront row · Asheville, NC",
    how: ["Anti-icing brine applied 24 hrs before forecasted event", "Plow route follows documented site map (no landscape damage)", "Walk crews shovel and salt entryways + accessible paths", "Post-storm inspection + top-up salt", "Photo debrief with GPS timestamp — insurance-grade"],
    equipment: "Plow trucks · walk-behind snowblowers · calcium chloride + rock salt · GPS-tracked crews",
  },
  "graffiti-removal": {
    blurb: "Response within 24 hours. Chemical and mechanical removal matched to substrate. Anti-graffiti clear-coat available as add-on.",
    image: null,
    location: "Alley · Bushwick, NY",
    how: ["24-hr response window", "Substrate test in inconspicuous area first", "Chemical remover matched to paint type + surface", "Warm-water pressure rinse", "Optional anti-graffiti clear coat for repeat-hit locations"],
    equipment: "Solvent removers · warm-water rig · protective PPE · sacrificial anti-graffiti coating",
  },
  "dumpster-cleaning": {
    blurb: "Interiors, exteriors, and pads pressure-washed, sanitized, and deodorized. Wastewater captured on-site — no runoff, no fines.",
    image: null,
    location: "Rear Service Yard · Queens, NY",
    how: ["Dumpster pull-back + pad exposed", "Hot-water interior + exterior blast (180°F+)", "Sanitizing + deodorizing chemistry", "Pad degreased + rinsed", "Wastewater captured via mat + vac recovery"],
    equipment: "Hot-water rig · enzymatic sanitizer · water-recovery mat + vacuum · deodorizing spray",
  },
  "outdoor-lighting": {
    blurb: "Permanent programmable exterior lighting. Warm holiday scenes, full architectural accent, or RGB — controlled from your phone.",
    image: null,
    location: "Boutique Hotel · Asheville, NC",
    how: ["Property lighting plan + scene mockup", "Discreet track installed along roofline / fascia", "Individually addressable RGBW LEDs (16M colors)", "App-controlled scenes (holidays, events, everyday warm-white)", "10-year LED lifespan + warranty"],
    equipment: "Permanent-hold LED track · smart controller · phone app · UL-listed low-voltage system",
  },
};

export default function ServiceLedger() {
  const [services, setServices] = useState([]);
  const [media, setMedia] = useState({});
  const [openIdx, setOpenIdx] = useState(0);

  useEffect(() => {
    axios.get(`${API}/services`).then((r) => setServices(r.data.services || []));
    axios.get(`${API}/media/services`).then((r) => {
      const raw = r.data.media || {};
      const base = process.env.REACT_APP_BACKEND_URL;
      const absolute = {};
      for (const [k, v] of Object.entries(raw)) {
        absolute[k] = v.startsWith("http") ? v : `${base}${v}`;
      }
      setMedia(absolute);
    }).catch(() => {});
  }, []);

  return (
    <section className="py-32 md:py-40" data-testid="service-ledger" style={{ background: "var(--bg)" }}>
      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-14">
        <div className="grid md:grid-cols-12 gap-6 mb-16">
          <div className="md:col-span-2 overline pt-2">Chapter Three</div>
          <div className="md:col-span-10">
            <h2 className="font-display text-5xl md:text-7xl leading-[0.95]" style={{ letterSpacing: "-0.03em" }}>
              The <span className="font-display-italic" style={{ color: "var(--gold-2)" }}>Service</span> Ledger
            </h2>
            <p className="mt-8 max-w-xl text-lg md:text-xl font-body" style={{ color: "var(--text-2)" }}>
              Ten disciplines, one accountable partner. Hover a row to open the file.
            </p>
          </div>
        </div>

        <div>
          {services.map((s, i) => {
            const meta = { ...(SERVICE_META[s.id] || {}), image: media[s.id] || SERVICE_META[s.id]?.image || null };
            const isOpen = openIdx === i;
            return (
              <div
                key={s.id}
                onMouseEnter={() => setOpenIdx(i)}
                onClick={() => setOpenIdx(i)}
                className="ledger-row group"
                data-testid={`ledger-row-${s.id}`}
              >
                <div className="font-mono text-sm md:text-base tabular-nums" style={{ color: "var(--text-3)" }}>
                  N° {String(i + 1).padStart(2, "0")}
                </div>
                <div className="min-w-0">
                  <div className="flex items-baseline gap-6 flex-wrap">
                    <div className="font-display text-3xl md:text-6xl leading-none kinetic" style={{ letterSpacing: "-0.02em", color: "var(--text)" }}>{s.name}</div>
                    <div className="hidden md:block font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--text-3)" }}>{s.tagline}</div>
                  </div>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pt-6 grid md:grid-cols-12 gap-8 items-start">
                          <div className="md:col-span-7">
                            <p className="text-base md:text-lg font-body max-w-xl" style={{ color: "var(--text-2)", lineHeight: 1.6 }}>
                              {meta.blurb || s.tagline}
                            </p>
                            {meta.how && meta.how.length > 0 && (
                              <div className="mt-8">
                                <div className="font-mono text-[10px] uppercase tracking-[0.32em] mb-4" style={{ color: "var(--gold)" }}>How it works</div>
                                <ol className="space-y-3 text-sm font-body" style={{ color: "var(--text-2)" }}>
                                  {meta.how.map((step, idx) => (
                                    <li key={idx} className="flex items-start gap-4">
                                      <span className="font-mono text-[10px] mt-0.5 tabular-nums" style={{ color: "var(--gold)" }}>0{idx + 1}</span>
                                      <span>{step}</span>
                                    </li>
                                  ))}
                                </ol>
                              </div>
                            )}
                            {meta.equipment && (
                              <div className="mt-8 p-4 glass-card" style={{ padding: "1rem 1.25rem" }}>
                                <div className="font-mono text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--gold)" }}>Equipment</div>
                                <div className="mt-2 font-body text-sm" style={{ color: "var(--text-2)" }}>{meta.equipment}</div>
                              </div>
                            )}
                            <Link to="/quote" className="btn-primary mt-6" data-testid={`ledger-quote-${s.id}`}>
                              Quote this service <ArrowUpRight size={14} className="arrow" />
                            </Link>
                          </div>
                          <div className="md:col-span-5">
                            <div className="aspect-[4/3] w-full overflow-hidden thin-border relative" style={{ borderColor: "var(--line-gold)", borderRadius: "var(--r-2xl)" }}>
                              {meta.image ? (
                                <>
                                  <img src={meta.image} alt={s.name} className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" style={{ filter: "brightness(0.85) contrast(1.05) saturate(0.9)" }} />
                                  <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg, transparent 50%, rgba(5,5,5,0.7))" }} />
                                  {meta.location && (
                                    <div className="absolute bottom-4 left-4 font-mono text-[9px] uppercase tracking-[0.32em] px-3 py-1.5" style={{ background: "rgba(5,5,5,0.85)", color: "var(--gold)", border: "1px solid var(--line-gold)", backdropFilter: "blur(8px)" }}>
                                      {meta.location}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <PhotoBriefPlaceholder brief={PHOTO_BRIEFS[s.id]} serviceName={s.name} />
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <div className="pl-6">
                  <ArrowUpRight size={22} style={{ color: isOpen ? "var(--gold)" : "var(--text-2)" }} className="transition-colors" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

